import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const ADMIN_PERMS = [
  "party_create", "purchase_bill_add", "purchase_bill_scan", "stock_edit",
  "customer_edit", "expense_manage", "pos_create", "sales_return",
  "barcode_print", "loyalty_adjust", "whatsapp_campaign_create", "whatsapp_billing_notif",
];
const CASHIER_PERMS = [
  "customer_create_pos", "discount_apply", "loyalty_redeem", "bill_hold",
  "bill_cancel", "whatsapp_bill_toggle",
];
const PAYMENT_MODES = ["Cash", "Card", "UPI", "Other"];

const blankForm = { name: "", email: "", phone: "", password: "", role: "admin", branch_ids: [], pos_terminal_id: "", permissions: {} };

export default function Users() {
  const { branches } = useAuth();
  const [items, setItems] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm);

  const load = async () => {
    // POS TERMINAL FEATURE — ON HOLD, DO NOT DELETE
    // const [u, t] = await Promise.all([api.get("/users"), api.get("/pos-terminals")]);
    const u = await api.get("/users");
    setItems(u.data); setTerminals([]);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form };
      if (!body.pos_terminal_id) delete body.pos_terminal_id;
      if (editing) {
        const { password, email, role, ...rest } = body;
        await api.put(`/users/${editing}`, rest);
        toast.success("Updated");
      } else {
        await api.post("/users", body);
        toast.success("User created");
      }
      setOpen(false); setEditing(null); setForm(blankForm); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const startEdit = (u) => {
    setEditing(u.id);
    setForm({ ...blankForm, ...u, password: "" });
    setOpen(true);
  };

  const permKeys = form.role === "admin" ? ADMIN_PERMS : form.role === "cashier" ? CASHIER_PERMS : [];

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(blankForm); } }}>
          <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="add-user-btn"><Plus className="w-4 h-4 mr-2" />Add User</Button></DialogTrigger>
          <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-auto">
            <DialogHeader><DialogTitle>{editing ? "Edit User" : "New User"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" data-testid="user-name" /></div>
                <div><Label>Phone</Label><Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none mt-1" data-testid="user-phone" placeholder="+91..." /></div>
                <div><Label>Email</Label><Input required type="email" disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none mt-1" data-testid="user-email" /></div>
                {!editing && <div><Label>Password</Label><Input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-none mt-1" data-testid="user-password" /></div>}
                <div><Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, permissions: {} })} disabled={!!editing}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {/* POS TERMINALS (device add) — ON HOLD, DO NOT DELETE.
                          Cashier role re-enabled: a cashier user IS a POS, no separate terminal record. */}
                      <SelectItem value="cashier">Cashier / POS</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* POS TERMINAL DEVICE DROPDOWN — ON HOLD, DO NOT DELETE
                {form.role === "cashier" && (
                  <div><Label>POS Terminal</Label>
                    <Select value={form.pos_terminal_id || ""} onValueChange={(v) => setForm({ ...form, pos_terminal_id: v })}>
                      <SelectTrigger className="rounded-none mt-1"><SelectValue placeholder="Select terminal" /></SelectTrigger>
                      <SelectContent>{terminals.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                */}
              </div>
              {form.role !== "super_admin" && (
                <div>
                  <Label>Branches</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {branches.map((b) => (
                      <label key={b.id} className="flex items-center gap-2 text-sm border border-slate-200 p-2">
                        <Checkbox checked={form.branch_ids.includes(b.id)} onCheckedChange={(chk) => {
                          const ids = chk ? [...form.branch_ids, b.id] : form.branch_ids.filter((x) => x !== b.id);
                          setForm({ ...form, branch_ids: ids });
                        }} data-testid={`branch-chk-${b.id}`} />
                        {b.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {permKeys.length > 0 && (
                <div>
                  <Label>Granular Permissions</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {permKeys.map((k) => (
                      <div key={k} className="flex items-center justify-between border border-slate-200 p-2 text-sm">
                        <span className="font-mono text-xs">{k}</span>
                        <Switch checked={!!form.permissions[k]} onCheckedChange={(v) => setForm({ ...form, permissions: { ...form.permissions, [k]: v } })} data-testid={`perm-${k}`} />
                      </div>
                    ))}
                  </div>
                  {form.role === "cashier" && (
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div><Label>Max Discount %</Label><Input type="number" value={form.permissions.max_discount_percent ?? 10} onChange={(e) => setForm({ ...form, permissions: { ...form.permissions, max_discount_percent: parseFloat(e.target.value) || 0 } })} className="rounded-none mt-1" /></div>
                      <div>
                        <Label>Payment Modes</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {PAYMENT_MODES.map((m) => {
                            const current = form.permissions.payment_modes || ["Cash", "Card", "UPI"];
                            const on = current.includes(m);
                            return <Badge key={m} onClick={() => {
                              const next = on ? current.filter((x) => x !== m) : [...current, m];
                              setForm({ ...form, permissions: { ...form.permissions, payment_modes: next } });
                            }} className={`rounded-none cursor-pointer ${on ? "bg-slate-900" : "bg-slate-200 text-slate-700 hover:bg-slate-300"}`}>{m}</Badge>;
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <DialogFooter><Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="user-save">{editing ? "Update" : "Create"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="border border-slate-200 bg-white rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            <th className="px-4 py-3 text-left font-semibold">Name</th><th className="px-4 py-3 text-left font-semibold">Email</th>
            <th className="px-4 py-3 text-left font-semibold">Phone</th><th className="px-4 py-3 text-left font-semibold">Role</th>
            <th className="px-4 py-3 text-left font-semibold">Branches</th><th className="px-4 py-3 text-right font-semibold">Actions</th>
          </tr></thead>
          <tbody>
            {items.map((u) => {
              const roleColor = u.role === "super_admin" ? "bg-slate-900 text-white" : u.role === "admin" ? "bg-blue-600 text-white" : "bg-green-600 text-white";
              return (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3 text-slate-600">{u.phone}</td>
                  <td className="px-4 py-3"><Badge className={`rounded-none ${roleColor}`}>{u.role.replace("_", " ")}</Badge></td>
                  <td className="px-4 py-3 text-xs text-slate-500">{(u.branch_ids || []).map((bid) => branches.find((b) => b.id === bid)?.name).filter(Boolean).join(", ") || "-"}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" className="rounded-none h-8 px-2" onClick={() => startEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="rounded-none h-8 px-2 text-red-600" onClick={() => remove(u.id)} data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
